// A simple hash function for the product ID to get a consistent seed value
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

// Seeded random number generator function
function seededRandom(seed: number): () => number {
  return function (): number {
    const x = Math.sin(seed++) * 10000
    return x - Math.floor(x)
  }
}

function _complexLogic(base: number, factor: number, randomFunc: () => number): number {
  return Math.round(base * randomFunc() * factor)
}

function getBaseCountFromHash(hash: number, scale = 10, minThreshold = 1): number {
  return Math.abs(hash % scale) + minThreshold
}

export function useProductMetrics(productId: string) {
  // Get the current day to change the random numbers once per day
  const daySeed = Math.floor(Date.now() / (1000 * 60 * 60 * 24))
  const productHash = hashString(productId) + daySeed
  const randomFunc = seededRandom(productHash)

  const currentTime = useState(() => Date.now())
  const hourOfDay = computed(() => new Date(currentTime.value).getHours())

  // Calculate baseCount
  const baseCountForSold = getBaseCountFromHash(productHash, 500, 250)
  const baseCountForRepeat = getBaseCountFromHash(productHash, 20, 10)
  const baseCountForViewing = getBaseCountFromHash(productHash, 50, 4)

  // Factor calculation
  const factorForSold = hourOfDay.value < 12 ? 0.8 : 1.5
  const factorForRepeat = (hourOfDay.value >= 12 && hourOfDay.value < 18) ? 1.2 : 0.9

  // Viewing count should be higher during peak hours (9AM-12PM and 6PM-9PM)
  const isPeakHour = (hourOfDay.value >= 9 && hourOfDay.value < 12) || (hourOfDay.value >= 18 && hourOfDay.value < 21)
  const factorForViewing = isPeakHour ? 1.7 : 0.7

  // Add small variation for viewing count that changes every few minutes
  const minuteSeed = Math.floor(Date.now() / (1000 * 60 * 5)) // Changes every 5 minutes
  const viewingVariation = seededRandom(productHash + minuteSeed)() * 0.3 + 0.85 // 0.85 to 1.15 range

  const generateCount = (baseCount: number, factor: number) => _complexLogic(baseCount, factor, randomFunc)

  const soldCount = computed(() => generateCount(baseCountForSold, factorForSold))
  const repeatPurchaseCount = computed(() => generateCount(baseCountForRepeat, factorForRepeat))
  const currentlyViewingCount = computed(() => {
    // Generate base viewing count
    const baseViewing = generateCount(baseCountForViewing, factorForViewing * viewingVariation)

    // Ensure viewing count is at least 1 to avoid showing "0 people viewing"
    return Math.max(1, baseViewing)
  })

  // Return only the computed properties, keeping the generation functions private
  return { soldCount, repeatPurchaseCount, currentlyViewingCount }
}
